/**
 * 角色卡社区页（T07b）。
 *
 * 路由 `#/community`，由 `src/app/router.tsx` 的 STANDALONE_ROUTES 追加接线；
 * 不占用底部导航，也不改既有四条主路由的行为。
 *
 * 能力：
 * - 列表：来自「可插拔来源」的卡片（名称 / 简介 / 标签 / 作者），支持关键字搜索与标签筛选；
 * - 详情：点击卡片进入只读预览（系统提示词摘要、世界书条目数、标签）；
 * - 导入：调 `fetchCard(id)` 拿到 Preset，以 `source: 'community'` 写入 presetRepo；
 *   id 冲突时弹二次确认，提供「覆盖导入 / 改名导入 / 取消」三条路径；
 * - 来源：默认内置目录（随包 JSON），可填入远端目录地址改用 HTTP 来源，也可切回内置；
 * - 每个可见部件都通过 `useComponentMeta` 登记进 `src/ui/registry.tsx`（390x844 逻辑坐标）。
 *
 * 动效：页面/视图切换 pageTransition、按压 tap、列表 listStagger，全部取自 `src/design/motion.ts`。
 *
 * 边界：本任务只做前端侧。真实社区后端 / 账号体系 / 上传审核未实现；远端来源仅按约定端点拉取 JSON。
 */
import { AnimatePresence, motion } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AriaRole, CSSProperties, ReactElement, ReactNode, RefObject } from 'react';

import { newId } from '../../core/ids';
import { getPreset, putPreset } from '../../core/repositories/presetRepo';
import type { ComponentMeta, Preset } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import type { PageDirection } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import {
  COMMUNITY_CARD_ITEM_HEIGHT,
  COMMUNITY_LIST_LAYOUT,
  COMMUNITY_TAG_BAR_LAYOUT,
  PAGE_ID,
  communityItemLayout,
  communityItemPartLayout,
  communityMetaById,
  communityTagLayout,
} from './componentMeta';
import type { CommunityCardPart } from './componentMeta';
import { contentBoxStyle, offsetBoxStyle } from './layout';
import { defaultSource } from './registry';
import { CommunitySourceError, createHttpSource } from './source';
import type { CommunityCardSummary, CommunitySource } from './source';
import styles from './community.module.css';

type ComponentRole = NonNullable<ComponentMetaInput['role']>;

const PROMPT_PREVIEW_LIMIT = 160;
const ALL_TAG = '_all_';
const BUILTIN_LABEL = '内置目录';

type ListState =
  | { status: 'loading' }
  | { status: 'ready'; cards: CommunityCardSummary[] }
  | { status: 'error'; message: string };

type DetailState =
  | { status: 'loading' }
  | { status: 'ready'; preset: Preset }
  | { status: 'error'; message: string };

interface ImportStatus {
  kind: 'success' | 'error';
  text: string;
}

interface ConflictState {
  preset: Preset;
  existingName: string;
}

/** 按 id 取静态部件元数据（渲染与清单同源）。 */
function m(id: string): ComponentMetaInput {
  return communityMetaById(id);
}

function describeError(error: unknown): string {
  if (error instanceof CommunitySourceError) return error.message;
  if (error instanceof Error) return error.message;
  return '未知错误';
}

function collectTags(cards: readonly CommunityCardSummary[]): string[] {
  const seen = new Set<string>();
  for (const card of cards) {
    for (const tag of card.tags) seen.add(tag);
  }
  return [...seen];
}

function matchesFilter(card: CommunityCardSummary, keyword: string, tag: string | null): boolean {
  if (tag !== null && !card.tags.includes(tag)) return false;
  const needle = keyword.trim().toLowerCase();
  if (needle === '') return true;
  return [card.name, card.description ?? '', ...card.tags].join(' ').toLowerCase().includes(needle);
}

interface Registered<T extends HTMLElement> {
  componentMeta: ComponentMeta;
  ref: RefObject<T>;
  style: CSSProperties;
}

/** 登记 + 计算绝对定位样式（parent 用于列表项内部部件）。 */
function useRegistered<T extends HTMLElement>(
  meta: ComponentMetaInput,
  fallbackRole: ComponentRole = 'container',
  parent?: BoxLayout,
): Registered<T> {
  const componentMeta = buildComponentMeta(meta, fallbackRole);
  const ref = useComponentMeta<T>(componentMeta);
  const style =
    parent === undefined ? contentBoxStyle(meta.layout) : offsetBoxStyle(meta.layout, parent);
  return { componentMeta, ref, style };
}

interface RegBlockProps {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  className?: string;
  children?: ReactNode;
  ariaRole?: AriaRole;
  ariaModal?: boolean;
  surface?: 'page' | 'panel' | 'control' | 'overlay';
}

function RegBlock({ meta, parent, className, children, ariaRole, ariaModal, surface }: RegBlockProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLDivElement>(meta, 'container', parent);
  return (
    <div
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      role={ariaRole}
      aria-modal={ariaModal === true ? true : undefined}
      aria-label={ariaRole === undefined ? undefined : componentMeta.label}
      data-skin-surface={surface}
      style={style}
    >
      {children}
    </div>
  );
}

interface RegTextProps {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  className?: string;
  children: ReactNode;
}

function RegHeading({ meta, className, children }: RegTextProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLHeadingElement>(meta, 'text');
  return (
    <h1
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
    >
      {children}
    </h1>
  );
}

function RegText({ meta, parent, className, children }: RegTextProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLParagraphElement>(meta, 'text', parent);
  return (
    <p
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
    >
      {children}
    </p>
  );
}

/** 渲染 <span>（可放在按钮内部，避免非法嵌套）。 */
function RegSpan({ meta, parent, className, children }: RegTextProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLSpanElement>(meta, 'text', parent);
  return (
    <span
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
    >
      {children}
    </span>
  );
}

interface RegButtonProps {
  meta: ComponentMetaInput;
  parent?: BoxLayout;
  className?: string;
  disabled?: boolean;
  ariaPressed?: boolean;
  variants?: Variants;
  onClick: () => void;
  children: ReactNode;
}

function RegButton({
  meta,
  parent,
  className,
  disabled,
  ariaPressed,
  variants,
  onClick,
  children,
}: RegButtonProps): ReactElement {
  const presets = useMotionPresets();
  const { componentMeta, ref, style } = useRegistered<HTMLButtonElement>(meta, 'button', parent);
  return (
    <motion.button
      ref={ref}
      type="button"
      disabled={disabled}
      aria-pressed={ariaPressed}
      aria-label={componentMeta.label}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
      variants={variants}
      whileTap={disabled === true ? undefined : presets.tap.whileTap}
      transition={presets.tap.transition}
      onClick={onClick}
    >
      {children}
    </motion.button>
  );
}

interface RegInputProps {
  meta: ComponentMetaInput;
  className?: string;
  value: string;
  placeholder?: string;
  autoComplete?: string;
  onChange: (value: string) => void;
}

function RegInput({ meta, className, value, placeholder, autoComplete, onChange }: RegInputProps): ReactElement {
  const { componentMeta, ref, style } = useRegistered<HTMLInputElement>(meta, 'input');
  return (
    <input
      ref={ref}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      type="text"
      value={value}
      placeholder={placeholder}
      autoComplete={autoComplete}
      aria-label={componentMeta.label}
      onChange={(event) => onChange(event.currentTarget.value)}
      style={style}
    />
  );
}

interface RegListProps {
  meta: ComponentMetaInput;
  className?: string;
  ariaLabel?: string;
  children?: ReactNode;
}

function RegList({ meta, className, ariaLabel, children }: RegListProps): ReactElement {
  const presets = useMotionPresets();
  const { componentMeta, ref, style } = useRegistered<HTMLDivElement>(meta, 'list');
  return (
    <motion.div
      ref={ref}
      role="list"
      aria-label={ariaLabel ?? componentMeta.label}
      className={className}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      style={style}
      variants={presets.listStagger.container}
      initial="hidden"
      animate="visible"
    >
      {children}
    </motion.div>
  );
}

interface CommunityCardProps {
  card: CommunityCardSummary;
  index: number;
  onOpen: (card: CommunityCardSummary) => void;
}

function CommunityCard({ card, index, onOpen }: CommunityCardProps): ReactElement {
  const presets = useMotionPresets();
  const itemLayout = communityItemLayout(index);
  const itemId = `community.card.${card.id}`;

  const cardMeta: ComponentMetaInput = {
    id: itemId,
    page: PAGE_ID,
    role: 'button',
    label: `查看角色卡：${card.name}`,
    layout: itemLayout,
    zIndex: 4,
    props: ['name', 'author', 'tags'],
    notes: '运行时卡片项；点击进入只读详情预览，安卓用 RecyclerView item',
  };

  const partMeta = (part: CommunityCardPart, label: string, role: ComponentRole): ComponentMetaInput => ({
    id: `${itemId}.${part}`,
    page: PAGE_ID,
    role,
    label,
    layout: communityItemPartLayout(itemLayout, part),
    zIndex: 5,
    notes: '卡片内部部件',
  });

  return (
    <RegButton
      meta={cardMeta}
      parent={COMMUNITY_LIST_LAYOUT}
      className={styles.card}
      variants={presets.listStagger.item}
      onClick={() => onOpen(card)}
    >
      <RegSpan meta={partMeta('avatar', '角色头像', 'icon')} parent={itemLayout} className={styles.cardAvatar}>
        {card.avatar ?? '🎭'}
      </RegSpan>
      <RegSpan meta={partMeta('name', '角色名称', 'text')} parent={itemLayout} className={styles.cardName}>
        {card.name}
      </RegSpan>
      <RegSpan meta={partMeta('author', '作者', 'text')} parent={itemLayout} className={styles.cardAuthor}>
        {card.author ?? '匿名'}
      </RegSpan>
      <RegSpan
        meta={partMeta('description', '角色简介', 'text')}
        parent={itemLayout}
        className={styles.cardDesc}
      >
        {card.description ?? '（无简介）'}
      </RegSpan>
      <RegSpan meta={partMeta('tags', '角色标签', 'text')} parent={itemLayout} className={styles.cardTags}>
        {card.tags.join(' · ')}
      </RegSpan>
    </RegButton>
  );
}

interface TagChipProps {
  label: string;
  tagId: string;
  index: number;
  active: boolean;
  onClick: () => void;
}

function TagChip({ label, tagId, index, active, onClick }: TagChipProps): ReactElement {
  const meta: ComponentMetaInput = {
    id: `community.tag.${tagId}`,
    page: PAGE_ID,
    role: 'button',
    label: `标签：${label}`,
    layout: communityTagLayout(index),
    zIndex: 4,
    props: ['tag', 'selected'],
    notes: '运行时标签筛选胶囊',
  };
  return (
    <RegButton
      meta={meta}
      parent={COMMUNITY_TAG_BAR_LAYOUT}
      className={active ? `${styles.tag} ${styles.tagActive}` : styles.tag}
      ariaPressed={active}
      onClick={onClick}
    >
      {label}
    </RegButton>
  );
}

export function CommunityPage(): ReactElement {
  const presets = useMotionPresets();
  const [source, setSource] = useState<CommunitySource>(() => defaultSource());
  const [sourceLabel, setSourceLabel] = useState<string>(() => defaultSource().label);
  const [remoteUrl, setRemoteUrl] = useState('');
  const [listState, setListState] = useState<ListState>({ status: 'loading' });
  const [keyword, setKeyword] = useState('');
  const [activeTag, setActiveTag] = useState<string | null>(null);
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [selected, setSelected] = useState<CommunityCardSummary | null>(null);
  const [detailState, setDetailState] = useState<DetailState>({ status: 'loading' });
  const [conflict, setConflict] = useState<ConflictState | null>(null);
  const [importStatus, setImportStatus] = useState<ImportStatus | null>(null);
  const [busy, setBusy] = useState(false);

  const loadCards = useCallback(async (next: CommunitySource): Promise<void> => {
    setSource(next);
    setSourceLabel(next.label);
    setActiveTag(null);
    setListState({ status: 'loading' });
    try {
      const cards = await next.list();
      setListState({ status: 'ready', cards });
    } catch (error) {
      setListState({ status: 'error', message: describeError(error) });
    }
  }, []);

  useEffect(() => {
    void loadCards(defaultSource());
  }, [loadCards]);

  const cards = useMemo(
    () => (listState.status === 'ready' ? listState.cards : []),
    [listState],
  );
  const tags = useMemo(() => collectTags(cards), [cards]);
  const filtered = useMemo(
    () => cards.filter((card) => matchesFilter(card, keyword, activeTag)),
    [cards, keyword, activeTag],
  );

  const openDetail = useCallback(
    (card: CommunityCardSummary): void => {
      setSelected(card);
      setView('detail');
      setImportStatus(null);
      setDetailState({ status: 'loading' });
      void (async () => {
        try {
          const preset = await source.fetchCard(card.id);
          setDetailState({ status: 'ready', preset });
        } catch (error) {
          setDetailState({ status: 'error', message: describeError(error) });
        }
      })();
    },
    [source],
  );

  const closeDetail = useCallback((): void => {
    setView('list');
    setSelected(null);
    setDetailState({ status: 'loading' });
    setImportStatus(null);
  }, []);

  const persist = useCallback(async (preset: Preset): Promise<void> => {
    try {
      await putPreset({ ...preset, source: 'community' });
      setImportStatus({ kind: 'success', text: `已导入「${preset.name}」到我的角色卡` });
      setConflict(null);
    } catch (error) {
      setImportStatus({ kind: 'error', text: `导入失败：${describeError(error)}` });
    }
  }, []);

  const handleImport = useCallback((): void => {
    if (detailState.status !== 'ready') return;
    const preset = detailState.preset;
    setBusy(true);
    void (async () => {
      try {
        const existing = await getPreset(preset.id);
        if (existing !== undefined) {
          setConflict({ preset, existingName: existing.name });
        } else {
          await persist(preset);
        }
      } catch (error) {
        setImportStatus({ kind: 'error', text: `导入失败：${describeError(error)}` });
      } finally {
        setBusy(false);
      }
    })();
  }, [detailState, persist]);

  const confirmOverwrite = useCallback((): void => {
    if (conflict === null) return;
    setBusy(true);
    void (async () => {
      try {
        await persist(conflict.preset);
      } finally {
        setBusy(false);
      }
    })();
  }, [conflict, persist]);

  const confirmRename = useCallback((): void => {
    if (conflict === null) return;
    const renamed: Preset = {
      ...conflict.preset,
      id: newId(),
      name: `${conflict.preset.name}（社区导入）`,
    };
    setBusy(true);
    void (async () => {
      try {
        await persist(renamed);
      } finally {
        setBusy(false);
      }
    })();
  }, [conflict, persist]);

  const handleLoadRemote = useCallback((): void => {
    const url = remoteUrl.trim();
    if (url === '') {
      setListState({ status: 'error', message: '请先填写远端目录地址' });
      return;
    }
    void loadCards(createHttpSource(url));
  }, [loadCards, remoteUrl]);

  const handleBuiltin = useCallback((): void => {
    void loadCards(defaultSource());
  }, [loadCards]);

  const retry = useCallback((): void => {
    void loadCards(source);
  }, [loadCards, source]);

  const renderList = (): ReactElement => (
    <>
      <RegBlock meta={m('page.community.root')} className={styles.page} surface="page" />
      <RegBlock meta={m('community.header')} className={styles.header}>
        <RegHeading meta={m('community.header.title')} className={styles.title}>
          栀歆角色卡
        </RegHeading>
      </RegBlock>
      <RegInput
        meta={m('community.searchInput')}
        className={styles.searchInput}
        value={keyword}
        placeholder="搜索名称 / 简介 / 标签"
        autoComplete="off"
        onChange={setKeyword}
      />
      <RegList meta={m('community.tagBar')} className={styles.tagBar} ariaLabel="标签筛选">
        <div style={{ width: (tags.length + 1) * 96 }} aria-hidden="true" />
        <TagChip
          label="全部"
          tagId={ALL_TAG}
          index={0}
          active={activeTag === null}
          onClick={() => setActiveTag(null)}
        />
        {tags.map((tag, index) => (
          <TagChip
            key={tag}
            label={tag}
            tagId={tag}
            index={index + 1}
            active={activeTag === tag}
            onClick={() => setActiveTag(activeTag === tag ? null : tag)}
          />
        ))}
      </RegList>
      <RegText meta={m('community.sourceLabel')} className={styles.sourceLabel}>
        栀歆角色卡来源（可填入返回 JSON 的远端目录地址）
      </RegText>
      <RegInput
        meta={m('community.sourceInput')}
        className={styles.sourceInput}
        value={remoteUrl}
        placeholder="例如 https://example.com/community"
        autoComplete="off"
        onChange={setRemoteUrl}
      />
      <RegButton
        meta={m('community.loadRemoteButton')}
        className={styles.loadButton}
        disabled={listState.status === 'loading'}
        onClick={handleLoadRemote}
      >
        {listState.status === 'loading' ? '加载中…' : '加载远端'}
      </RegButton>
      <RegButton
        meta={m('community.builtinButton')}
        className={styles.builtinButton}
        ariaPressed={sourceLabel === BUILTIN_LABEL}
        onClick={handleBuiltin}
      >
        切回内置目录
      </RegButton>
      <RegText meta={m('community.sourceHint')} className={styles.sourceHint}>
        {`来源：${sourceLabel}`}
      </RegText>

      {listState.status === 'loading' ? (
        <RegBlock meta={m('community.state.loading')} className={styles.state}>
          <RegText meta={m('community.state.loading.text')} className={styles.stateTitle}>
            正在加载栀歆角色卡…
          </RegText>
        </RegBlock>
      ) : null}

      {listState.status === 'error' ? (
        <RegBlock meta={m('community.state.error')} className={styles.state}>
          <RegText meta={m('community.state.error.text')} className={styles.stateHint}>
            {`加载失败：${listState.message}`}
          </RegText>
          <RegButton meta={m('community.state.retryButton')} className={styles.retryButton} onClick={retry}>
            重试
          </RegButton>
        </RegBlock>
      ) : null}

      {listState.status === 'ready' && filtered.length === 0 ? (
        <RegBlock meta={m('community.state.empty')} className={styles.state}>
          <RegText meta={m('community.state.empty.text')} className={styles.stateTitle}>
            没有找到匹配的角色卡
          </RegText>
        </RegBlock>
      ) : null}

      {listState.status === 'ready' && filtered.length > 0 ? (
        <RegList meta={m('community.list')} className={styles.list} ariaLabel="栀歆角色卡列表">
          <div style={{ height: filtered.length * COMMUNITY_CARD_ITEM_HEIGHT }} aria-hidden="true" />
          {filtered.map((card, index) => (
            <CommunityCard key={card.id} card={card} index={index} onOpen={openDetail} />
          ))}
        </RegList>
      ) : null}
    </>
  );

  const renderDetail = (): ReactElement => {
    const summary = selected;
    const preset = detailState.status === 'ready' ? detailState.preset : null;
    const prompt = preset?.systemPrompt ?? '';
    const truncated = prompt.length > PROMPT_PREVIEW_LIMIT;
    const promptPreview = truncated ? `${prompt.slice(0, PROMPT_PREVIEW_LIMIT)}…` : prompt;
    const worldBookCount = preset?.worldBook?.length ?? 0;

    return (
      <>
        <RegBlock meta={m('page.community.detail')} className={styles.page} surface="page" />
        <RegBlock meta={m('community.detail.header')} className={styles.header}>
          <RegButton meta={m('community.detail.backButton')} className={styles.backButton} onClick={closeDetail}>
            ←
          </RegButton>
          <RegHeading meta={m('community.detail.title')} className={styles.detailTitle}>
            角色卡预览
          </RegHeading>
        </RegBlock>
        <RegSpan meta={m('community.detail.avatar')} className={styles.detailAvatar}>
          {summary?.avatar ?? '🎭'}
        </RegSpan>
        <RegText meta={m('community.detail.name')} className={styles.detailName}>
          {summary?.name ?? '未知角色卡'}
        </RegText>
        <RegText meta={m('community.detail.author')} className={styles.detailAuthor}>
          {`作者：${summary?.author ?? '匿名'}`}
        </RegText>
        <RegText meta={m('community.detail.tags')} className={styles.detailTags}>
          {summary === null || summary.tags.length === 0 ? '无标签' : summary.tags.join(' · ')}
        </RegText>
        <RegText meta={m('community.detail.promptLabel')} className={styles.promptLabel}>
          系统提示词（只读摘要）
        </RegText>
        <RegText meta={m('community.detail.prompt')} className={styles.prompt}>
          {detailState.status === 'loading'
            ? '正在读取角色卡…'
            : detailState.status === 'error'
              ? `读取失败：${detailState.message}`
              : promptPreview}
        </RegText>
        <RegText meta={m('community.detail.promptMore')} className={styles.promptMore}>
          {truncated ? '（已截断，导入后可在角色卡中查看全文）' : ''}
        </RegText>
        <RegText meta={m('community.detail.worldBook')} className={styles.worldBook}>
          {`世界书条目：${worldBookCount} 条`}
        </RegText>
        <RegText
          meta={m('community.detail.status')}
          className={
            importStatus?.kind === 'error'
              ? `${styles.status} ${styles.statusError}`
              : importStatus?.kind === 'success'
                ? `${styles.status} ${styles.statusSuccess}`
                : styles.status
          }
        >
          {importStatus?.text ?? ''}
        </RegText>
        <RegButton meta={m('community.detail.cancelButton')} className={styles.cancelButton} onClick={closeDetail}>
          取消
        </RegButton>
        <RegButton
          meta={m('community.detail.importButton')}
          className={styles.importButton}
          disabled={detailState.status !== 'ready' || busy}
          onClick={handleImport}
        >
          {busy ? '导入中…' : '导入到我的角色卡'}
        </RegButton>
      </>
    );
  };

  const renderConflict = (): ReactElement | null => {
    if (conflict === null) return null;
    return (
      <>
        <RegBlock
          meta={m('community.conflict.backdrop')}
          className={styles.backdrop}
          ariaRole="presentation"
        />
        <RegBlock
          meta={m('community.conflict.dialog')}
          className={styles.dialog}
          ariaRole="dialog"
          ariaModal
        >
          <RegText meta={m('community.conflict.title')} className={styles.dialogTitle}>
            id 冲突
          </RegText>
          <RegText meta={m('community.conflict.message')} className={styles.dialogMessage}>
            {`已存在同 id 的角色卡「${conflict.existingName}」。请选择「覆盖导入」或「改名导入」。`}
          </RegText>
          <RegButton
            meta={m('community.conflict.overwriteButton')}
            className={styles.dialogPrimary}
            disabled={busy}
            onClick={confirmOverwrite}
          >
            覆盖导入
          </RegButton>
          <RegButton
            meta={m('community.conflict.renameButton')}
            className={styles.dialogSecondary}
            disabled={busy}
            onClick={confirmRename}
          >
            改名导入
          </RegButton>
          <RegButton
            meta={m('community.conflict.cancelButton')}
            className={styles.dialogSecondary}
            disabled={busy}
            onClick={() => setConflict(null)}
          >
            取消导入
          </RegButton>
        </RegBlock>
      </>
    );
  };

  const direction: PageDirection = view === 'detail' ? 'forward' : 'backward';

  return (
    <AnimatePresence initial={false} custom={direction}>
      <motion.div
        key={view}
        custom={direction}
        variants={presets.pageTransition}
        initial="enter"
        animate="center"
        exit="exit"
        style={{ position: 'absolute', inset: 0 }}
      >
        {view === 'list' ? renderList() : renderDetail()}
        {renderConflict()}
      </motion.div>
    </AnimatePresence>
  );
}
